import * as React from 'react';
import { ArrowRightLeft, Check, ChevronDown } from 'lucide-react';

import { cn } from 'lib/utils';
import { Badge, type BadgeProps } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { cardVariants } from 'components/ui/card';

/**
 * Puerto real de `TransferStatusCard.tsx` (chirigota, `src/components/MiBeneficioV2/`,
 * usado por `IndividualDashboardV2.tsx` en la sección "Trámites") — el traspaso, en
 * una sola card COLAPSABLE. Separada a propósito de la verificación de identidad
 * (esa vive solo en `ui/CardAction`, con su propio CTA): esta card
 * nunca abre el KYC, solo informa del estado del traspaso.
 *
 * Solo la PARTE VISUAL se porta aquí — el real deriva `variant`/`title`/`subtitle`/
 * `badge` de datos de backend (estado del traspaso + si la identidad está
 * verificada), que no tiene sentido reproducir como lógica de UI: cada pantalla que
 * use esto ya resuelve esos valores antes de pasarlos.
 *
 * REGLA DE NEGOCIO REAL — "se contrae al verificar identidad": nace desplegada
 * (`defaultExpanded=true`) mientras la identidad no está verificada, para que se vea
 * el detalle sin un clic de más; en cuanto se verifica, nace CONTRAÍDA — ya no
 * compite por atención con la acción que el usuario puede hacer ahora
 * (configurar aportación). Se puede desplegar a mano en cualquier momento.
 *
 * REGLA DE NEGOCIO REAL — "nuevo plan" no es un traspaso: cuando el título es de
 * variante "nuevo plan" (`Activación pendiente` / `Activando tu plan` / `Plan
 * activado`), ni el importe ni el origen se muestran junto al título — ambos son
 * propios de un traspaso real. Ver `titleNewPlan` más abajo.
 *
 * REGLA DE NEGOCIO REAL — el estado "completed" no es un componente aparte (antes
 * `ui/TransferConfirmedBanner`): es un ESTADO más de esta misma card — mismo fondo
 * blanco que el resto, pero con el icono en el mismo patrón que `MetricCardV2`
 * (círculo verde clarito + icono en verde principal, en vez del neutro de
 * flechas). En el real (chirigota) ese estado además se ve UNA sola vez
 * (localStorage) — lógica de aplicación que no se reproduce aquí, solo la parte
 * visual, como el resto de este puerto.
 */
/**
 * Los seis estados de un traspaso en chirigota (`TransferStatus`). `requested`: registrado, aún sin
 * salir hacia la gestora. `rejected` y `cancelled` son terminales.
 */
export type TransferCardVariant =
	'requested' | 'pending' | 'inProgress' | 'completed' | 'rejected' | 'cancelled';

/**
 * Tono del badge por estado. `pending` espera algo del usuario (aviso); `rejected` y `cancelled`
 * son terminales (error); `completed`, éxito; el resto, neutro. El error va en `destructive-text`
 * sobre el fondo de la card (4,63:1) y no sobre `destructive-surface`, que en claro se queda en
 * 3,90:1, por debajo de AA para un texto de 10 px. Por lo mismo el neutro es el `outline` del
 * Badge (`foreground`) y no `muted-foreground`.
 */
const BADGE: Record<TransferCardVariant, { variant: BadgeProps['variant']; className?: string }> = {
	requested: { variant: 'outline' },
	pending: { variant: 'warning' },
	inProgress: { variant: 'outline' },
	completed: { variant: 'success' },
	rejected: { variant: 'outline', className: 'border-destructive/30 text-destructive-text' },
	cancelled: { variant: 'outline', className: 'border-destructive/30 text-destructive-text' }
};

export interface TransferStatusCardProps {
	variant: TransferCardVariant;
	title: string;
	subtitle: string;
	badge: string;
	/** Solo traspaso real — nunca en "nuevo plan" (ver nota de negocio arriba). */
	amount?: string;
	/** Solo traspaso real — nunca en "nuevo plan". */
	originName?: string;
	fromLabel?: string;
	/** Ignorado con `directAccessCta`: esa card no se despliega. */
	defaultExpanded?: boolean;
	/** El detalle que se despliega. Ignorado con `directAccessCta`. */
	children?: React.ReactNode;
	/**
	 * Card PUENTE a otro plan del mismo usuario: el detalle del traspaso vive en ESE plan, no aquí,
	 * así que no se despliega. En vez de chevron, un botón de acceso directo; `children` y
	 * `defaultExpanded` se ignoran.
	 */
	directAccessCta?: { label: string; onClick: () => void };
	className?: string;
	/**
	 * Pensado para cuando esto vive DENTRO de `ui/PlanCard` (columna de planes
	 * de Resumen, ~280-320px) en vez de en la página de detalle de un plan
	 * (900px). Sin esto, con ese ancho el título envuelve a 3 líneas — probado
	 * en Storybook, no una suposición. Quita el icono (el mayor consumidor de
	 * ancho horizontal), reduce el padding y el badge. `compact` tampoco es
	 * colapsable: dentro de una card de Resumen el detalle está siempre
	 * visible, sin cabecera-botón ni chevron — a diferencia del uso en la
	 * página de detalle de un plan, que sí se puede contraer.
	 */
	compact?: boolean;
}

export const TransferStatusCard = ({
	variant,
	title,
	subtitle,
	badge,
	amount,
	originName,
	fromLabel = 'desde',
	defaultExpanded = true,
	children,
	className,
	compact = false,
	directAccessCta
}: TransferStatusCardProps) => {
	const [expanded, setExpanded] = React.useState(defaultExpanded);
	// Para `aria-controls`: el botón dice qué panel abre y cierra.
	const idDetalle = React.useId();
	const badgeTone = BADGE[variant];
	// Desplegable y sin `children`, con la TARJETA por debajo de `@xl` (36rem): rejilla de dos filas
	// (icono · texto · chevron arriba; el badge debajo, en la columna del texto). En una sola fila el badge
	// (`shrink-0`, con textos como «Pendiente de tu verificación») se quedaba el ancho y el título salía
	// letra a letra. Se mide la tarjeta (`@container`) y no la pantalla: una tarjeta estrecha en una
	// pantalla ancha (una columna, el preview de Storybook) se rompía igual. Más ancha, la fila de siempre.
	// La columna del chevron solo existe si hay chevron: vacía, su hueco le quitaría 16 px al texto.
	const enDosFilasSiEstrecha = !compact && !directAccessCta;
	const iconoEstrecho = '@max-xl:w-3.5 @max-xl:h-3.5';
	const filaCabecera = (conChevron: boolean) =>
		cn(
			'w-full flex items-center gap-4 p-5 @xl:p-6 text-left @max-xl:grid @max-xl:items-start @max-xl:gap-y-2',
			conChevron
				? '@max-xl:grid-cols-[auto_minmax(0,1fr)_auto]'
				: '@max-xl:grid-cols-[auto_minmax(0,1fr)]'
		);

	const header = (
		<>
			{/* Confirmado: mismo patrón que MetricCardV2 (círculo verde clarito +
			    icono en verde principal, en vez del neutro de flechas), para que
			    se note de un vistazo que este ya no es un trámite en curso, sino
			    uno resuelto. Oculto en `compact`: es el mayor consumidor de ancho
			    horizontal y esta variante vive en columnas estrechas. */}
			{!compact && (
				<span
					className={cn(
						'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
						variant === 'completed' ? 'bg-primary/10' : 'bg-muted',
						// En dos filas, el tamaño `sm` de IconBadge (32 px) y arriba: el círculo empieza donde
						// empiezan las letras del título. En su línea de 22 px (`leading-snug`) las mayúsculas
						// arrancan 4,8 px más abajo con Space Grotesk (cabecera-botón) y 5,4 px con Inter (sin
						// detalle): baja 5 px.
						enDosFilasSiEstrecha && '@max-xl:w-8 @max-xl:h-8 @max-xl:mt-1.25'
					)}
				>
					{variant === 'completed' ? (
						<Check className={cn('w-5 h-5 text-primary', enDosFilasSiEstrecha && iconoEstrecho)} />
					) : (
						<ArrowRightLeft
							className={cn('w-5 h-5 text-muted-foreground', enDosFilasSiEstrecha && iconoEstrecho)}
						/>
					)}
				</span>
			)}
			{/* Con acceso directo, un ancho mínimo para el texto: en una rejilla estrecha el botón salta
			    de línea en vez de aplastar el título a una palabra por línea. */}
			<div className={cn('flex-1 min-w-0', directAccessCta && 'basis-48')}>
				{/* `compact`: amount/originName NO se concatenan al título — en la columna
				    estrecha del carrusel de planes (240-280px, el ancho real que este modo
				    documenta) ese párrafo libre envolvía a 3-4 líneas. Van en su propia línea
				    truncada debajo, igual de informativos, nunca más de 1 línea. */}
				<p
					className={cn(
						'font-bold text-foreground',
						compact ? 'text-sm' : 'text-base leading-snug'
					)}
				>
					{title}
					{!compact && amount && ` · ${amount}`}
					{!compact && originName && ` ${fromLabel} ${originName}`}
				</p>
				{compact && (amount || originName) && (
					<p className="text-xs text-muted-foreground truncate">
						{amount}
						{amount && originName && ` ${fromLabel} `}
						{originName}
					</p>
				)}
				{subtitle && (
					<p className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>{subtitle}</p>
				)}
			</div>
			{/* El tono sale de `BADGE` (arriba), con las variantes del propio Badge. `outline` y no
			    `secondary` para el neutro: `--secondary` es rosa/verde según tema en este sistema,
			    no gris (ver Badge "converted_no_reward" en Screens/Invita y Gana). */}
			<Badge
				variant={badgeTone.variant}
				className={cn(
					'font-heading font-medium tracking-wider uppercase shrink-0',
					// Segunda fila con la tarjeta estrecha: desde la columna del texto hasta el borde, a su ancho.
					enDosFilasSiEstrecha &&
						'@max-xl:col-[2/-1] @max-xl:row-start-2 @max-xl:justify-self-start',
					badgeTone.className,
					compact ? 'text-[9px] px-1.5 py-0' : 'text-[10px]'
				)}
			>
				{badge}
			</Badge>
		</>
	);

	return (
		<div
			className={cn(cardVariants({ variant: 'elevated' }), '@container overflow-hidden', className)}
		>
			{directAccessCta ? (
				<div
					className={cn(
						'w-full flex flex-wrap items-center text-left',
						compact ? 'gap-2 p-3' : 'gap-4 p-5 sm:p-6'
					)}
				>
					{header}
					<Button
						variant="outline"
						size={compact ? 'sm' : 'default'}
						onClick={directAccessCta.onClick}
						className="shrink-0 max-sm:w-full"
					>
						{directAccessCta.label}
					</Button>
				</div>
			) : compact ? (
				// items-start, no items-center: con el título (que puede envolver) arriba y
				// la línea de amount/originName debajo, el bloque de texto es más alto
				// que el badge — centrarlo verticalmente lo dejaba flotando a media altura en
				// vez de alineado con la primera línea.
				<div className="w-full flex items-start gap-2 p-3 text-left">{header}</div>
			) : !children ? (
				// Sin detalle no hay nada que desplegar: ni botón ni chevron, que prometerían algo.
				<div className={filaCabecera(false)}>{header}</div>
			) : (
				<button
					type="button"
					className={filaCabecera(true)}
					onClick={() => setExpanded((v) => !v)}
					// Sin esto, que la card está abierta o cerrada solo lo decía el chevron al girar.
					aria-expanded={expanded}
					aria-controls={idDetalle}
				>
					{header}
					<ChevronDown
						// En dos filas, arriba con la primera línea del título: 1 px para centrarlo en sus 22.
						className={cn(
							'w-5 h-5 text-muted-foreground shrink-0 transition-transform @max-xl:mt-px',
							{ 'rotate-180': expanded }
						)}
					/>
				</button>
			)}

			{/* Montado también contraído (`hidden`): así el id de `aria-controls` existe siempre, y lo
			    que haya dentro conserva su estado al cerrar y abrir. */}
			{!directAccessCta && children && (
				<div
					id={idDetalle}
					hidden={!compact && !expanded}
					className={cn(
						'border-t border-border grid grid-cols-1 gap-6',
						// Dos columnas solo si la tarjeta las tiene (`@3xl`, 48rem), no la pantalla: en una
						// tarjeta estrecha, el detalle del traspaso se quedaba en media tarjeta y se pisaba.
						compact ? 'p-3 pt-3 gap-3' : 'p-5 @xl:p-6 pt-6 @3xl:grid-cols-2'
					)}
				>
					{children}
				</div>
			)}
		</div>
	);
};
